import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomPagination from "../../../../../components/CustomPagination";
import Modal from "../../../../../components/Modal";
import color from "../../../../../constants/color";
import { useAlert } from "../../../../../context/AlertContext";
import style from "./style";

dayjs.extend(utc);

function AllReconciliationListPage({
  accountID,
  bankName,
  data,
  page,
  setPage,
  total,
  handleDeleteAccountReconciliation,
  handleUndoAccountReconciliation,
}) {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openReconcileModal, setOpenReconcileModal] = useState(false);
  const [startDate, setStartDate] = useState(dayjs.utc().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs.utc().endOf("month"));
  const [closingBalance, setClosingBalance] = useState("");
  const [selected, setSelected] = useState([]);
  const [lastClosingBalance, setLastClosingBalance] = useState(0);
  const { showAlert } = useAlert();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openUndoDialog, setOpenUndoDialog] = useState(false);

  const navigate = useNavigate();

  // Add reconcile modal close handler
  const handleReconcileModalClose = () => {
    setOpenReconcileModal(false);
  };

  const handleOpenReconcileModal = () => {
    if (data.length > 0) {
      const latestReconciliation = data[0];
      if (latestReconciliation.status === "in-progress") {
        showAlert(
          "You have an in-progress reconciliation. Please complete it before starting a new one.",
          "warning"
        );
        return; // Don't proceed further
      }

      const nextStart = dayjs
        .utc(latestReconciliation.endDate)
        .add(1, "day")
        .startOf("day");
      setStartDate(nextStart);
    } else {
      setStartDate(dayjs.utc().startOf("month"));
    }

    setEndDate(dayjs.utc().endOf("month"));
    setClosingBalance("");
    setOpenReconcileModal(true);
  };

  const handleReconcileSubmit = () => {
    handleReconcileModalClose();
    navigate("/dashboard/accountReconciliation", {
      state: {
        bankName: bankName,
        _id: accountID,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        closingBalance,
        lastClosingBalance,
      },
      replace: false,
    });
  };

  const getId = (row, idx) => row._id ?? row.id ?? idx;

  const handleToggle = useCallback(
    (id) => (e) => {
      e.stopPropagation();
      setSelected((prev) =>
        prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
      );
    },
    []
  );

  const handleToggleAll = (e) => {
    e.stopPropagation();
    const pageIds = data.map(getId);
    const allOnPage = pageIds.every((id) => selected.includes(id));

    setSelected((prev) =>
      allOnPage
        ? prev.filter((id) => !pageIds.includes(id))
        : [...prev, ...pageIds.filter((id) => !prev.includes(id))]
    );
  };

  const allChecked =
    data.length > 0 && data.every((r, i) => selected.includes(getId(r, i)));
  const indeterminate = selected.length > 0 && !allChecked;

  // Store selected transactions and their totals for reuse
  const selectedTransactions = useMemo(
    () => data.filter((item, idx) => selected.includes(getId(item, idx))),
    [data, selected]
  );

  useEffect(() => {
    if (data.length > 0) {
      const nextStart = dayjs.utc(data[0].endDate).add(1, "day").startOf("day");
      setStartDate(nextStart);
      setLastClosingBalance(data[0].closingBalance);
    }
  }, [data]);

  const handleReconciliationClick = async (item) => {
    if (item.status === "in-progress") {
      const lastBalance = data.length > 1 ? data[1]?.closingBalance ?? 0 : 0;
      navigate("/dashboard/accountReconciliation", {
        state: {
          _id: item.account,
          startDate: item.startDate,
          endDate: item.endDate,
          closingBalance: item.closingBalance,
          bankName,
          lastClosingBalance: lastBalance,
          type: "finalizable",
          reconciliationId: item._id,
        },
      });
    } else {
      navigate("/dashboard/reconciliationDetails", {
        state: {
          reconciliationId: item._id,
          bankName,
        },
      });
    }
  };

  const handleMenuClick = (event, item, idx) => {
    event.stopPropagation();
    if (!item) return;

    setAnchorEl(event.currentTarget);
    setSelectedMenuItem(item);
    setSelectedIndex(idx);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Actions
  const handleUndo = () => {
    setOpenUndoDialog(true);
    setTimeout(() => handleMenuClose(), 0); // keep selectedMenuItem
  };

  const handlePrint = () => {
    handleMenuClose();
  };

  const handleDelete = () => {
    setOpenDeleteDialog(true);
    // Delay menu close so selectedMenuItem is still available
    setTimeout(() => handleMenuClose(), 0);
  };

  const handleDeleteReconciliationData = () => {
    handleDeleteAccountReconciliation(selectedMenuItem._id);
    setOpenDeleteDialog(false);
  };

  const handleUndoReconciliation = () => {
    handleUndoAccountReconciliation(selectedMenuItem._id);
    setOpenUndoDialog(false);
  };

  return (
    <>
      <Box
        px="15px"
        pt="20px"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h5" sx={style.heading}>
          {bankName} - Reconciliation
        </Typography>

        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            color="success"
            sx={style.checkinBtn}
            onClick={handleOpenReconcileModal}
          >
            Start Reconciliation
          </Button>
        </Box>
      </Box>

      <Box sx={{ backgroundColor: color.card, m: 2, borderRadius: "20px" }}>
        <Box
          sx={{
            p: "18px",
            fontSize: "18px",
            fontWeight: "600",
            m: "0 5px 5px 0",
          }}
        >
          Reconciliations
        </Box>
        <Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={style.TableHead}>
                    <Checkbox
                      sx={{ p: 0, m: 0 }}
                      checked={allChecked}
                      indeterminate={indeterminate}
                      onClick={handleToggleAll}
                    />
                  </TableCell>
                  <TableCell sx={style.TableHead}>Reconciled Date</TableCell>
                  <TableCell sx={style.TableHead}>
                    Reconiliation Period
                  </TableCell>
                  <TableCell sx={style.TableHead}>Closing Balance</TableCell>
                  <TableCell sx={style.TableHead} align="right">
                    Status
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="h6">No data</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {data.map((item, idx) => {
                      const rowId = getId(item, idx);
                      return (
                        <TableRow
                          key={rowId}
                          hover
                          sx={{
                            cursor: "pointer",
                            "&:hover .action-icon": {
                              visibility: "visible",
                            },
                          }}
                          onClick={() => {
                            handleReconciliationClick(item);
                          }}
                        >
                          <TableCell sx={style.tableData}>
                            <Checkbox
                              sx={{ p: 0, m: 0 }}
                              checked={selected.includes(rowId)}
                              onClick={handleToggle(rowId)}
                            />
                          </TableCell>
                          <TableCell sx={style.tableData}>
                            {item?.reconciledDate
                              ? dayjs
                                  .utc(item?.reconciledDate)
                                  .format("YYYY-MM-DD")
                              : dayjs.utc(item?.createdAt).format("YYYY-MM-DD")}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...style.tableData,
                              maxWidth: 120,
                              minWidth: 100,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {`${dayjs
                              .utc(item?.startDate)
                              .format("YYYY-MM-DD")} to ${dayjs
                              .utc(item?.endDate)
                              .format("YYYY-MM-DD")}`}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...style.tableData,
                              width: 120,
                              maxWidth: 140,
                              minWidth: 100,
                            }}
                            align="right"
                          >
                            {`$${item?.closingBalance}`}
                          </TableCell>
                          <TableCell sx={style.tableData} align="right">
                            {item?.status}
                          </TableCell>
                          <TableCell sx={style.tableData} align="left">
                            <IconButton
                              onClick={(e) => handleMenuClick(e, item, idx)}
                              className="action-icon"
                              sx={{ visibility: "hidden" }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                          <TableCell sx={style.tableData}></TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                )}
              </TableBody>
            </Table>
            <CustomPagination
              pageCount={total}
              totalItems={total}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
          </TableContainer>
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {data.length > 0 &&
          selectedIndex === 0 &&
          selectedMenuItem?.status === "in-progress" && (
            <MenuItem onClick={handleDelete}>Delete</MenuItem>
          )}

        {data.length > 0 &&
          selectedIndex === 0 &&
          selectedMenuItem?.status === "reconciled" && (
            <>
              <MenuItem onClick={handleUndo}>Undo</MenuItem>
              <MenuItem onClick={handleDelete}>Delete</MenuItem>
            </>
          )}

        {selectedIndex > 0 && <MenuItem onClick={handlePrint}>Print</MenuItem>}
      </Menu>

      {/* Reconcile Account Modal */}
      <Modal
        open={openReconcileModal}
        setOpen={handleReconcileModalClose}
        disabled={!startDate || !endDate || !closingBalance}
        btnText={"Start Reconciliation"}
        title={"Reconciliation"}
        handleCheck={handleReconcileSubmit}
        modalStyle={{
          "& .MuiDialog-paper": {
            width: "400px",
            borderRadius: "20px",
            pr: "12px",
          },
          "& .MuiDialogActions-root": {
            px: "12px",
            mr: "2px",
          },
          "& .MuiDialogActions-root button": {
            width: "100%",
          },
        }}
        btnWidth={{ width: "100%" }}
      >
        <Box sx={{ ...style.inputBoxs, width: "100%" }}>
          <Typography sx={style.inputTexts}>Start Date</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
              <DatePicker
                value={startDate}
                slotProps={{
                  field: {
                    readOnly: true,
                  },
                }}
                sx={style.calenderInput}
                onChange={(newValue) => {
                  const normalizedStart = dayjs.utc(newValue).startOf("day");
                  setStartDate(normalizedStart);
                }}
                disabled={data.length > 0}
              />
            </Box>
          </LocalizationProvider>
        </Box>
        <Box sx={{ ...style.inputBoxs, width: "100%" }}>
          <Typography sx={style.inputTexts}>End Date</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
              <DatePicker
                value={endDate}
                minDate={startDate}
                slotProps={{
                  field: {
                    readOnly: true,
                  },
                }}
                sx={style.calenderInput}
                onChange={(newValue) => {
                  const normalizedEnd = dayjs.utc(newValue).endOf("day");
                  setEndDate(normalizedEnd);
                }}
              />
            </Box>
          </LocalizationProvider>
        </Box>
        <Box sx={{ width: "100%", ml: "5px" }}>
          <InputLabel sx={style.label}>Closing Balance</InputLabel>
          <TextField
            name="closingBalance"
            value={closingBalance}
            type="number"
            required
            onChange={(e) => {
              setClosingBalance(e.target.value);
            }}
            sx={style.boxs2}
            placeholder="Enter closing balance"
          />
        </Box>
      </Modal>

      {/* Reconcile Delete Modal */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        sx={{
          "& .MuiDialog-paper": {
            width: "400px",
            borderRadius: "20px",
            pr: "12px",
          },
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this reconciliation?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            variant="contained"
            color="success"
            sx={style.checkinBtn}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteReconciliationData}
            sx={{ borderRadius: 50 }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reconcile Undo Modal */}
      <Dialog
        open={openUndoDialog}
        onClose={() => setOpenUndoDialog(false)}
        sx={{
          "& .MuiDialog-paper": {
            width: "400px",
            borderRadius: "20px",
            pr: "12px",
          },
        }}
      >
        <DialogTitle>Confirm Undo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to undo this reconciliation?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenUndoDialog(false)}
            variant="contained"
            color="success"
            sx={style.checkinBtn}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUndoReconciliation}
            sx={{ borderRadius: 50 }}
            color="warning"
            variant="contained"
          >
            Undo
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AllReconciliationListPage;
